import {
  ChevronDown,
  ChevronRight,
  File,
  Folder,
  FolderInput,
  FolderOpen,
  MoreHorizontal,
  Pencil,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import {
  DOC_PERMISSION_RANK,
  type DocFolderWithAccess,
  type DocPage,
} from "@estelle/domain";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { buildFolderTree, type FolderNode } from "@/modules/docs/tree";

interface FolderTreeProps {
  folders: readonly DocFolderWithAccess[];
  /** Omit to render folders only — Permissions selects a folder, not a page. */
  pagesByFolder?: ReadonlyMap<string, DocPage[]>;
  selectedFolderId?: string | null;
  onSelectFolder?: (folderId: string) => void;
  selectedPageId?: string | null;
  onSelectPage?: (page: DocPage, folderId: string) => void;
  /**
   * The folder row's "⋯" menu. All optional and independent: `Permissions`
   * passes none of them, since it is already the grants screen and a
   * shortcut back to itself would be a menu item for nowhere. `Browse`
   * passes every one. `onViewDeleted` only ever renders for a folder the
   * caller holds Manage on (§7.8, FR-DOC-13) — a courtesy, not the
   * enforcement; `GET .../deleted-pages` answers empty regardless.
   */
  onRenameFolder?: (folderId: string, currentName: string) => void;
  onManageFolderAccess?: (folderId: string) => void;
  onViewDeleted?: (folderId: string) => void;
  /** "Move to…" on a folder itself (FR-DOC-10, #414), reusing `moveFolder`. */
  onMoveFolder?: (folder: DocFolderWithAccess) => void;
  /**
   * A page row's "⋯" menu (FR-DOC-10, FR-DOC-13). Both optional and
   * independent, the same shape as the folder menu above.
   */
  onMovePage?: (page: DocPage) => void;
  onDeletePage?: (page: DocPage) => void;
  /** Replaces "No folders." — a filtered tree with nothing left says so differently. */
  emptyMessage?: string;
}

interface NodeRowProps {
  node: FolderNode;
  depth: number;
  collapsed: ReadonlySet<string>;
  onToggle: (folderId: string) => void;
  pagesByFolder?: ReadonlyMap<string, DocPage[]>;
  selectedFolderId?: string | null;
  onSelectFolder?: (folderId: string) => void;
  selectedPageId?: string | null;
  onSelectPage?: (page: DocPage, folderId: string) => void;
  onRenameFolder?: (folderId: string, currentName: string) => void;
  onManageFolderAccess?: (folderId: string) => void;
  onViewDeleted?: (folderId: string) => void;
  onMoveFolder?: (folder: DocFolderWithAccess) => void;
  onMovePage?: (page: DocPage) => void;
  onDeletePage?: (page: DocPage) => void;
}

function NodeRow({
  node,
  depth,
  collapsed,
  onToggle,
  pagesByFolder,
  selectedFolderId,
  onSelectFolder,
  selectedPageId,
  onSelectPage,
  onRenameFolder,
  onManageFolderAccess,
  onViewDeleted,
  onMoveFolder,
  onMovePage,
  onDeletePage,
}: NodeRowProps) {
  const isCollapsed = collapsed.has(node.folder.id);
  const pages = pagesByFolder?.get(node.folder.id) ?? [];
  const hasChildren = node.children.length > 0 || pages.length > 0;
  const indent = `${depth * 0.9 + 0.25}rem`;
  const holdsManage =
    DOC_PERMISSION_RANK[node.folder.effectiveLevel] >=
    DOC_PERMISSION_RANK.manage;
  const holdsWrite =
    DOC_PERMISSION_RANK[node.folder.effectiveLevel] >=
    DOC_PERMISSION_RANK.write;
  const showDeletedItem = Boolean(onViewDeleted) && holdsManage;
  // A courtesy, not the enforcement — `moveFolder`, `movePage` and
  // `DELETE /pages/:id` each check the folder themselves regardless of what
  // this menu offers. Hiding the item here just spares a click that would
  // only ever come back `forbidden`, the same reason `PageReader`'s own mode
  // toggle is hidden for a Read-only visitor.
  const showMoveFolderItem = Boolean(onMoveFolder) && holdsWrite;
  const showMenu = Boolean(
    onRenameFolder ||
    onManageFolderAccess ||
    showMoveFolderItem ||
    showDeletedItem,
  );
  const showMoveItem = Boolean(onMovePage) && holdsWrite;
  const showDeleteItem = Boolean(onDeletePage) && holdsManage;
  const showPageMenu = showMoveItem || showDeleteItem;

  return (
    <li>
      <div
        className={cn(
          "group flex items-center gap-1 rounded-md py-1 pr-1 text-sm",
          onSelectFolder && "cursor-pointer hover:bg-muted/50",
          onSelectFolder && selectedFolderId === node.folder.id && "bg-accent",
        )}
        style={{ paddingLeft: indent }}
        onClick={
          onSelectFolder ? () => onSelectFolder(node.folder.id) : undefined
        }
      >
        <button
          type="button"
          aria-label={
            isCollapsed
              ? `Expand ${node.folder.name}`
              : `Collapse ${node.folder.name}`
          }
          className="text-muted-foreground shrink-0"
          onClick={(event) => {
            event.stopPropagation();
            if (hasChildren) onToggle(node.folder.id);
          }}
        >
          {hasChildren ? (
            isCollapsed ? (
              <ChevronRight className="size-3.5" aria-hidden />
            ) : (
              <ChevronDown className="size-3.5" aria-hidden />
            )
          ) : (
            <span className="inline-block size-3.5" />
          )}
        </button>
        {isCollapsed ? (
          <Folder
            className="text-muted-foreground size-4 shrink-0"
            aria-hidden
          />
        ) : (
          <FolderOpen
            className="text-muted-foreground size-4 shrink-0"
            aria-hidden
          />
        )}
        <span className="min-w-0 flex-1 truncate">{node.folder.name}</span>

        {showMenu ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className={cn(
                  "text-muted-foreground shrink-0",
                  // Quiet until hovered (#641), like a page row's.
                  "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 pointer-coarse:opacity-100",
                )}
                aria-label={`More actions for ${node.folder.name}`}
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                <MoreHorizontal className="size-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              {onRenameFolder ? (
                <DropdownMenuItem
                  onSelect={() => {
                    onRenameFolder(node.folder.id, node.folder.name);
                  }}
                >
                  <Pencil /> Rename
                </DropdownMenuItem>
              ) : null}
              {showMoveFolderItem ? (
                <DropdownMenuItem
                  onSelect={() => {
                    onMoveFolder?.(node.folder);
                  }}
                >
                  <FolderInput /> Move to…
                </DropdownMenuItem>
              ) : null}
              {onManageFolderAccess ? (
                <DropdownMenuItem
                  onSelect={() => {
                    onManageFolderAccess(node.folder.id);
                  }}
                >
                  <ShieldCheck /> Share
                </DropdownMenuItem>
              ) : null}
              {showDeletedItem ? (
                <DropdownMenuItem
                  onSelect={() => {
                    onViewDeleted?.(node.folder.id);
                  }}
                >
                  <Trash2 /> Trash
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>

      {!isCollapsed ? (
        <ul>
          {pagesByFolder
            ? pages.map((page) => (
                <li key={page.id}>
                  <div
                    className={cn(
                      "group flex items-center gap-1 rounded-md pr-1 text-sm",
                      "hover:bg-muted/50",
                      selectedPageId === page.id && "bg-accent",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => onSelectPage?.(page, node.folder.id)}
                      style={{
                        paddingLeft: `${(depth + 1) * 0.9 + 0.25}rem`,
                      }}
                      className={cn(
                        "flex min-w-0 flex-1 items-center gap-1.5 py-1 text-left",
                        selectedPageId === page.id && "font-medium",
                      )}
                    >
                      <File
                        className="text-muted-foreground size-3.5 shrink-0"
                        aria-hidden
                      />
                      <span className="truncate">{page.title}</span>
                    </button>

                    {showPageMenu ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            className={cn(
                              "text-muted-foreground shrink-0",
                              // Out of the way until hovered or its own menu
                              // is open — a page row does not need this
                              // clutter every time the tree is at rest.
                              "opacity-0 focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100 pointer-coarse:opacity-100",
                            )}
                            aria-label={`More actions for ${page.title}`}
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                          >
                            <MoreHorizontal className="size-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                        >
                          {showMoveItem ? (
                            <DropdownMenuItem
                              onSelect={() => {
                                onMovePage?.(page);
                              }}
                            >
                              <FolderInput /> Move to…
                            </DropdownMenuItem>
                          ) : null}
                          {showMoveItem && showDeleteItem ? (
                            <DropdownMenuSeparator />
                          ) : null}
                          {showDeleteItem ? (
                            <DropdownMenuItem
                              variant="destructive"
                              onSelect={() => {
                                onDeletePage?.(page);
                              }}
                            >
                              <Trash2 /> Delete
                            </DropdownMenuItem>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </div>
                </li>
              ))
            : null}
          {node.children.map((child) => (
            <NodeRow
              key={child.folder.id}
              node={child}
              depth={depth + 1}
              collapsed={collapsed}
              onToggle={onToggle}
              pagesByFolder={pagesByFolder}
              selectedFolderId={selectedFolderId}
              onSelectFolder={onSelectFolder}
              selectedPageId={selectedPageId}
              onSelectPage={onSelectPage}
              onRenameFolder={onRenameFolder}
              onManageFolderAccess={onManageFolderAccess}
              onViewDeleted={onViewDeleted}
              onMoveFolder={onMoveFolder}
              onMovePage={onMovePage}
              onDeletePage={onDeletePage}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * The folder tree every Docs screen shares — Browse selects a page from it,
 * Permissions selects a folder.
 *
 * It renders exactly what `DocsRepository.listFolders` returned. FR-DOC-9
 * makes a folder someone cannot read absent from that list, not locked, so
 * there is nothing here to grey out or mark with a lock icon — a folder
 * either appears, readable, or it was never sent down at all.
 */
export function FolderTree({
  folders,
  pagesByFolder,
  selectedFolderId,
  onSelectFolder,
  selectedPageId,
  onSelectPage,
  onRenameFolder,
  onManageFolderAccess,
  onViewDeleted,
  onMoveFolder,
  onMovePage,
  onDeletePage,
  emptyMessage = "No folders.",
}: FolderTreeProps) {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const tree = buildFolderTree(folders);

  function toggle(folderId: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  }

  if (tree.length === 0) {
    return (
      <p className="text-muted-foreground px-2 py-4 text-sm">{emptyMessage}</p>
    );
  }

  return (
    <ul className="space-y-0.5">
      {tree.map((node) => (
        <NodeRow
          key={node.folder.id}
          node={node}
          depth={0}
          collapsed={collapsed}
          onToggle={toggle}
          pagesByFolder={pagesByFolder}
          selectedFolderId={selectedFolderId}
          onSelectFolder={onSelectFolder}
          selectedPageId={selectedPageId}
          onSelectPage={onSelectPage}
          onRenameFolder={onRenameFolder}
          onManageFolderAccess={onManageFolderAccess}
          onViewDeleted={onViewDeleted}
          onMoveFolder={onMoveFolder}
          onMovePage={onMovePage}
          onDeletePage={onDeletePage}
        />
      ))}
    </ul>
  );
}
